import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './AdminAddService.css';

function AdminAddService() {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    duration: '',
    price: '',
    description: '',
    type: 'Remote',
    category: '',
    image: '',
    hasAudio: false,
    pdfUrl: '',
    audioUrl: '',
    // Arabic translations
    name_ar: '',
    description_ar: '',
    duration_ar: '',
    // Russian translations
    name_ru: '',
    description_ru: '',
    duration_ru: '',
    // German translations
    name_de: '',
    description_de: '',
    duration_de: '',
    // French translations
    name_fr: '',
    description_fr: '',
    duration_fr: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Load service data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadServiceData();
    }
  }, [id, isEditMode]);

  const loadServiceData = async () => {
    try {
      setIsLoadingData(true);
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        setFormData({
          name: data.name || '',
          duration: data.duration || '',
          price: data.price || '',
          description: data.description || '',
          type: data.type || 'Remote',
          category: data.category || '',
          image: data.image || '',
          hasAudio: data.has_audio || false,
          pdfUrl: data.pdf_url || '',
          audioUrl: data.audio_url || '',
          // Arabic translations
          name_ar: data.name_ar || '',
          description_ar: data.description_ar || '',
          duration_ar: data.duration_ar || '',
          // Russian translations
          name_ru: data.name_ru || '',
          description_ru: data.description_ru || '',
          duration_ru: data.duration_ru || '',
          // German translations
          name_de: data.name_de || '',
          description_de: data.description_de || '',
          duration_de: data.duration_de || '',
          // French translations
          name_fr: data.name_fr || '',
          description_fr: data.description_fr || '',
          duration_fr: data.duration_fr || ''
        });
        // Set image preview if image URL exists
        if (data.image) {
          setImagePreview(data.image);
        }
      }
    } catch (err) {
      console.error('Error loading service:', err);
      setErrorMessage('Failed to load service data. Please try again.');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    // Handle file upload
    if (type === 'file' && files && files[0]) {
      const file = files[0];
      const inputName = e.target.name;
      
      if (inputName === 'image') {
        // Validate image file type
        if (!file.type.startsWith('image/')) {
          setErrors(prev => ({
            ...prev,
            image: 'Please select an image file'
          }));
          return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setErrors(prev => ({
            ...prev,
            image: 'Image size must be less than 5MB'
          }));
          return;
        }
        
        setImageFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
        
        // Clear errors
        if (errors.image) {
          setErrors(prev => ({
            ...prev,
            image: ''
          }));
        }
      } else if (inputName === 'pdf') {
        // Validate PDF file type
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
          setErrors(prev => ({
            ...prev,
            pdf: 'Please select a PDF file'
          }));
          return;
        }
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          setErrors(prev => ({
            ...prev,
            pdf: 'PDF size must be less than 10MB'
          }));
          return;
        }
        
        setPdfFile(file);
        
        // Clear errors
        if (errors.pdf) {
          setErrors(prev => ({
            ...prev,
            pdf: ''
          }));
        }
      } else if (inputName === 'audio') {
        // Validate audio file type (MP3)
        const validAudioTypes = ['audio/mpeg', 'audio/mp3', 'audio/mpeg3', 'audio/x-mpeg-3'];
        const isValidType = validAudioTypes.includes(file.type) || file.name.toLowerCase().endsWith('.mp3');
        
        if (!isValidType) {
          setErrors(prev => ({
            ...prev,
            audio: 'Please select an MP3 audio file'
          }));
          return;
        }
        
        // Validate file size (max 20MB for audio)
        if (file.size > 20 * 1024 * 1024) {
          setErrors(prev => ({
            ...prev,
            audio: 'Audio file size must be less than 20MB'
          }));
          return;
        }
        
        setAudioFile(file);
        
        // Clear errors
        if (errors.audio) {
          setErrors(prev => ({
            ...prev,
            audio: ''
          }));
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      // Clear errors when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
    
    // Clear messages
    if (successMessage) setSuccessMessage('');
    if (errorMessage) setErrorMessage('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Service name is required';
    }

    if (!formData.duration.trim()) {
      newErrors.duration = 'Duration is required';
    }

    if (!formData.price.trim()) {
      newErrors.price = 'Price is required';
    }
    // Price can be any text - no validation needed

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }

    if (!formData.type) {
      newErrors.type = 'Service type is required';
    }

    // Image is only required when adding new service, not when editing
    if (!isEditMode && !imageFile) {
      newErrors.image = 'Please select an image file';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (validateForm()) {
      setIsLoading(true);
      setUploadProgress(0);

      try {
        let imageUrl = formData.image; // Keep existing image if editing and no new file
        let pdfUrl = formData.pdfUrl; // Keep existing PDF if editing and no new file
        let audioUrl = formData.audioUrl; // Keep existing audio if editing and no new file

        // Upload image to Supabase Storage (only if new file is selected)
        if (imageFile) {
          const fileExt = imageFile.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          // File path should NOT include bucket name, just the filename
          const filePath = fileName;

          // Upload file
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('service-images')
            .upload(filePath, imageFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Upload error details:', uploadError);
            throw new Error(`Image upload failed: ${uploadError.message}`);
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('service-images')
            .getPublicUrl(filePath);

          imageUrl = urlData.publicUrl;
        }

        // Upload PDF to Supabase Storage (only if new file is selected)
        if (pdfFile) {
          const fileExt = pdfFile.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = fileName;

          // Upload file
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('service-pdfs')
            .upload(filePath, pdfFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('PDF upload error details:', uploadError);
            throw new Error(`PDF upload failed: ${uploadError.message}`);
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('service-pdfs')
            .getPublicUrl(filePath);

          pdfUrl = urlData.publicUrl;
        }

        // Upload audio to Supabase Storage (only if new file is selected)
        if (audioFile) {
          const fileExt = audioFile.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = fileName;

          // Upload file
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('service-audio')
            .upload(filePath, audioFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Audio upload error details:', uploadError);
            throw new Error(`Audio upload failed: ${uploadError.message}`);
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('service-audio')
            .getPublicUrl(filePath);

          audioUrl = urlData.publicUrl;
        }
        
        // Set progress to 100% after all uploads complete
        setUploadProgress(100);

        // Use price as-is (any text allowed)
        let formattedPrice = formData.price.trim();

        // Update or insert service into Supabase
        let result;
        if (isEditMode) {
          // Update existing service
          const updateData = {
            name: formData.name.trim(),
            duration: formData.duration.trim(),
            price: formattedPrice,
            description: formData.description.trim(),
            type: formData.type,
            category: formData.category?.trim() || null,
            has_audio: formData.hasAudio
          };
          
          // Add translation fields
          if (formData.name_ar.trim()) updateData.name_ar = formData.name_ar.trim();
          if (formData.description_ar.trim()) updateData.description_ar = formData.description_ar.trim();
          if (formData.duration_ar.trim()) updateData.duration_ar = formData.duration_ar.trim();
          if (formData.name_ru.trim()) updateData.name_ru = formData.name_ru.trim();
          if (formData.description_ru.trim()) updateData.description_ru = formData.description_ru.trim();
          if (formData.duration_ru.trim()) updateData.duration_ru = formData.duration_ru.trim();
          if (formData.name_de.trim()) updateData.name_de = formData.name_de.trim();
          if (formData.description_de.trim()) updateData.description_de = formData.description_de.trim();
          if (formData.duration_de.trim()) updateData.duration_de = formData.duration_de.trim();
          if (formData.name_fr.trim()) updateData.name_fr = formData.name_fr.trim();
          if (formData.description_fr.trim()) updateData.description_fr = formData.description_fr.trim();
          if (formData.duration_fr.trim()) updateData.duration_fr = formData.duration_fr.trim();
          
          // Only update image if a new file was uploaded
          if (imageUrl) {
            updateData.image = imageUrl;
          }
          
          // Only update PDF if a new file was uploaded
          if (pdfUrl) {
            updateData.pdf_url = pdfUrl;
          } else if (pdfFile === null && !formData.pdfUrl) {
            // If PDF file was removed, set to null
            updateData.pdf_url = null;
          }
          
          // Only update audio if a new file was uploaded
          if (audioUrl) {
            updateData.audio_url = audioUrl;
          } else if (audioFile === null && !formData.audioUrl) {
            // If audio file was removed, set to null
            updateData.audio_url = null;
          }
          
          const { data, error } = await supabase
            .from('services')
            .update(updateData)
            .eq('id', id)
            .select();

          if (error) {
            throw error;
          }
          result = data;
          setSuccessMessage('Service updated successfully!');
        } else {
          // Insert new service
          const insertData = {
            name: formData.name.trim(),
            duration: formData.duration.trim(),
            price: formattedPrice,
            description: formData.description.trim(),
            type: formData.type,
            category: formData.category?.trim() || null,
            image: imageUrl,
            has_audio: formData.hasAudio,
            pdf_url: pdfUrl || null,
            audio_url: audioUrl || null
          };
          
          // Add translation fields if provided
          if (formData.name_ar.trim()) insertData.name_ar = formData.name_ar.trim();
          if (formData.description_ar.trim()) insertData.description_ar = formData.description_ar.trim();
          if (formData.duration_ar.trim()) insertData.duration_ar = formData.duration_ar.trim();
          if (formData.name_ru.trim()) insertData.name_ru = formData.name_ru.trim();
          if (formData.description_ru.trim()) insertData.description_ru = formData.description_ru.trim();
          if (formData.duration_ru.trim()) insertData.duration_ru = formData.duration_ru.trim();
          if (formData.name_de.trim()) insertData.name_de = formData.name_de.trim();
          if (formData.description_de.trim()) insertData.description_de = formData.description_de.trim();
          if (formData.duration_de.trim()) insertData.duration_de = formData.duration_de.trim();
          if (formData.name_fr.trim()) insertData.name_fr = formData.name_fr.trim();
          if (formData.description_fr.trim()) insertData.description_fr = formData.description_fr.trim();
          if (formData.duration_fr.trim()) insertData.duration_fr = formData.duration_fr.trim();
          
          const { data, error } = await supabase
            .from('services')
            .insert([insertData])
            .select();

          if (error) {
            throw error;
          }
          result = data;
          setSuccessMessage('Service added successfully!');
        }

        // Reset form only if adding new service
        if (!isEditMode) {
          setFormData({
            name: '',
            duration: '',
            price: '',
            description: '',
            type: 'Remote',
            category: '',
            image: '',
            hasAudio: false,
            pdfUrl: '',
            audioUrl: '',
            name_ar: '',
            description_ar: '',
            duration_ar: '',
            name_ru: '',
            description_ru: '',
            duration_ru: '',
            name_de: '',
            description_de: '',
            duration_de: '',
            name_fr: '',
            description_fr: '',
            duration_fr: ''
          });
          setImageFile(null);
          setImagePreview(null);
          setPdfFile(null);
          setAudioFile(null);
          setUploadProgress(0);
          // Reset file inputs
          const imageInput = document.getElementById('image');
          if (imageInput) imageInput.value = '';
          const pdfInput = document.getElementById('pdf');
          if (pdfInput) pdfInput.value = '';
          const audioInput = document.getElementById('audio');
          if (audioInput) audioInput.value = '';
        } else {
          // If editing, update image preview if new image was uploaded
          if (imageUrl) {
            setImagePreview(imageUrl);
            setImageFile(null);
          }
          // Clear PDF file state if uploaded
          if (pdfUrl) {
            setPdfFile(null);
          }
          // Clear audio file state if uploaded
          if (audioUrl) {
            setAudioFile(null);
          }
        }
        
        // Redirect to services list after 2 seconds
        setTimeout(() => {
          navigate('/admin-dashboard/services');
        }, 2000);
      } catch (err) {
        console.error('Error adding service:', err);
        setErrorMessage(err.message || 'Failed to add service. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="admin-add-service-page">
      {isLoadingData ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading service data...</p>
        </div>
      ) : (
        <>
          <div className="add-service-header">
            <h1 className="add-service-title">{isEditMode ? 'Edit Service' : 'Add New Service'}</h1>
            <p className="add-service-subtitle">
              {isEditMode ? 'Update service information' : 'Create a new service to offer to your clients'}
            </p>
          </div>

          <div className="add-service-form-container">
            {successMessage && (
              <div className="success-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                {successMessage}
              </div>
            )}

            {errorMessage && (
              <div className="error-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {errorMessage}
              </div>
            )}

            <form className="add-service-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Service Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder="e.g., Reiki Healing Session"
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="duration" className="form-label">
                Duration <span className="required">*</span>
              </label>
              <input
                type="text"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className={`form-input ${errors.duration ? 'error' : ''}`}
                placeholder="e.g., 60 minutes"
              />
              {errors.duration && <span className="error-text">{errors.duration}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price" className="form-label">
                Price <span className="required">*</span>
              </label>
              <input
                type="text"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className={`form-input ${errors.price ? 'error' : ''}`}
                placeholder="e.g., $120, 120 KWD, Contact for pricing, etc."
              />
              {errors.price && <span className="error-text">{errors.price}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="type" className="form-label">
                Service Type <span className="required">*</span>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={`form-input form-select ${errors.type ? 'error' : ''}`}
              >
                <option value="Remote">Remote</option>
                <option value="On-Call">On-Call</option>
              </select>
              {errors.type && <span className="error-text">{errors.type}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Session Category <span className="optional-text">(Optional)</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category || ''}
                onChange={handleChange}
                className={`form-input form-select ${errors.category ? 'error' : ''}`}
              >
                <option value="">-- Select Category (Optional) --</option>
                <option value="Healing Session">Healing Session</option>
                <option value="Training Session">Training Session</option>
              </select>
              {errors.category && <span className="error-text">{errors.category}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`form-input form-textarea ${errors.description ? 'error' : ''}`}
              placeholder="Describe the service in detail..."
              rows="5"
            />
            {errors.description && <span className="error-text">{errors.description}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="image" className="form-label">
              Service Image {!isEditMode && <span className="required">*</span>}
              {isEditMode && <span className="optional-text">(Optional - leave empty to keep current image)</span>}
            </label>
            <div className="file-upload-container">
              <input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                onChange={handleChange}
                className={`file-input ${errors.image ? 'error' : ''}`}
              />
              <label htmlFor="image" className="file-upload-label">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <span>
                  {imageFile 
                    ? imageFile.name 
                    : (isEditMode && formData.image ? 'Change Image' : 'Choose Image File')
                  }
                </span>
              </label>
            </div>
            {errors.image && <span className="error-text">{errors.image}</span>}
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" />
                {imageFile && (
                  <div className="image-info">
                    <span>{imageFile.name}</span>
                    <span>{(imageFile.size / 1024 / 1024).toFixed(2)} MB</span>
                  </div>
                )}
              </div>
            )}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="upload-progress">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                <span className="progress-text">Uploading... {uploadProgress}%</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="pdf" className="form-label">
              PDF Document (Optional)
              {isEditMode && formData.pdfUrl && <span className="optional-text"> - Current PDF will be replaced</span>}
            </label>
            <div className="file-upload-container">
              <input
                type="file"
                id="pdf"
                name="pdf"
                accept="application/pdf,.pdf"
                onChange={handleChange}
                className={`file-input ${errors.pdf ? 'error' : ''}`}
              />
              <label htmlFor="pdf" className="file-upload-label">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span>
                  {pdfFile 
                    ? pdfFile.name 
                    : (isEditMode && formData.pdfUrl ? 'Change PDF' : 'Choose PDF File')
                  }
                </span>
              </label>
            </div>
            {errors.pdf && <span className="error-text">{errors.pdf}</span>}
            {isEditMode && formData.pdfUrl && !pdfFile && (
              <div className="pdf-preview">
                <a 
                  href={formData.pdfUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="pdf-link"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                  View Current PDF
                </a>
                <button
                  type="button"
                  className="remove-pdf-btn"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, pdfUrl: '' }));
                    setPdfFile(null);
                    const pdfInput = document.getElementById('pdf');
                    if (pdfInput) pdfInput.value = '';
                  }}
                >
                  Remove PDF
                </button>
              </div>
            )}
            {pdfFile && (
              <div className="pdf-info">
                <span>{pdfFile.name}</span>
                <span>{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="audio" className="form-label">
              Audio File (MP3) (Optional)
              {isEditMode && formData.audioUrl && <span className="optional-text"> - Current audio will be replaced</span>}
            </label>
            <div className="file-upload-container">
              <input
                type="file"
                id="audio"
                name="audio"
                accept="audio/mpeg,audio/mp3,.mp3"
                onChange={handleChange}
                className={`file-input ${errors.audio ? 'error' : ''}`}
              />
              <label htmlFor="audio" className="file-upload-label">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18V5l12-2v13"></path>
                  <circle cx="6" cy="18" r="3"></circle>
                  <circle cx="18" cy="16" r="3"></circle>
                </svg>
                <span>
                  {audioFile 
                    ? audioFile.name 
                    : (isEditMode && formData.audioUrl ? 'Change Audio File' : 'Choose MP3 Audio File')
                  }
                </span>
              </label>
            </div>
            {errors.audio && <span className="error-text">{errors.audio}</span>}
            {isEditMode && formData.audioUrl && !audioFile && (
              <div className="audio-preview">
                <audio controls src={formData.audioUrl} className="audio-player">
                  Your browser does not support the audio element.
                </audio>
                <a 
                  href={formData.audioUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="audio-link"
                  download
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download Audio
                </a>
                <button
                  type="button"
                  className="remove-audio-btn"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, audioUrl: '' }));
                    setAudioFile(null);
                    const audioInput = document.getElementById('audio');
                    if (audioInput) audioInput.value = '';
                  }}
                >
                  Remove Audio
                </button>
              </div>
            )}
            {audioFile && (
              <div className="audio-info">
                <span>{audioFile.name}</span>
                <span>{(audioFile.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
            )}
          </div>

          {/* Translation Section */}
          <div className="translation-section">
            <h3 className="translation-section-title">Translations (Optional)</h3>
            <p className="translation-section-note">
              Add translations to display services in Arabic, Russian, German, and French. If not provided, the system will use translations from the translation file if available.
            </p>

            {/* Arabic Translations */}
            <div className="translation-group">
              <h4 className="translation-group-title">Arabic Translations</h4>
              <div className="form-group">
                <label htmlFor="name_ar" className="form-label">
                  Service Name (Arabic)
                </label>
                <input
                  type="text"
                  id="name_ar"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="اسم الخدمة بالعربية"
                />
              </div>
              <div className="form-group">
                <label htmlFor="duration_ar" className="form-label">
                  Duration (Arabic)
                </label>
                <input
                  type="text"
                  id="duration_ar"
                  name="duration_ar"
                  value={formData.duration_ar}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="المدة بالعربية"
                />
              </div>
              <div className="form-group">
                <label htmlFor="description_ar" className="form-label">
                  Description (Arabic)
                </label>
                <textarea
                  id="description_ar"
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleChange}
                  className="form-input form-textarea"
                  placeholder="وصف الخدمة بالعربية"
                  rows="4"
                />
              </div>
            </div>

            {/* Russian Translations */}
            <div className="translation-group">
              <h4 className="translation-group-title">Russian Translations</h4>
              <div className="form-group">
                <label htmlFor="name_ru" className="form-label">
                  Service Name (Russian)
                </label>
                <input
                  type="text"
                  id="name_ru"
                  name="name_ru"
                  value={formData.name_ru}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Название услуги на русском"
                />
              </div>
              <div className="form-group">
                <label htmlFor="duration_ru" className="form-label">
                  Duration (Russian)
                </label>
                <input
                  type="text"
                  id="duration_ru"
                  name="duration_ru"
                  value={formData.duration_ru}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Продолжительность на русском"
                />
              </div>
              <div className="form-group">
                <label htmlFor="description_ru" className="form-label">
                  Description (Russian)
                </label>
                <textarea
                  id="description_ru"
                  name="description_ru"
                  value={formData.description_ru}
                  onChange={handleChange}
                  className="form-input form-textarea"
                  placeholder="Описание услуги на русском"
                  rows="4"
                />
              </div>
            </div>

            {/* German Translations */}
            <div className="translation-group">
              <h4 className="translation-group-title">German Translations</h4>
              <div className="form-group">
                <label htmlFor="name_de" className="form-label">
                  Service Name (German)
                </label>
                <input
                  type="text"
                  id="name_de"
                  name="name_de"
                  value={formData.name_de}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Service-Name auf Deutsch"
                />
              </div>
              <div className="form-group">
                <label htmlFor="duration_de" className="form-label">
                  Duration (German)
                </label>
                <input
                  type="text"
                  id="duration_de"
                  name="duration_de"
                  value={formData.duration_de}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Dauer auf Deutsch (z.B., 1 Stunde)"
                />
              </div>
              <div className="form-group">
                <label htmlFor="description_de" className="form-label">
                  Description (German)
                </label>
                <textarea
                  id="description_de"
                  name="description_de"
                  value={formData.description_de}
                  onChange={handleChange}
                  className="form-input form-textarea"
                  placeholder="Beschreibung des Services auf Deutsch"
                  rows="4"
                />
              </div>
            </div>

            {/* French Translations */}
            <div className="translation-group">
              <h4 className="translation-group-title">French Translations</h4>
              <div className="form-group">
                <label htmlFor="name_fr" className="form-label">
                  Service Name (French)
                </label>
                <input
                  type="text"
                  id="name_fr"
                  name="name_fr"
                  value={formData.name_fr}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Nom du service en français"
                />
              </div>
              <div className="form-group">
                <label htmlFor="duration_fr" className="form-label">
                  Duration (French)
                </label>
                <input
                  type="text"
                  id="duration_fr"
                  name="duration_fr"
                  value={formData.duration_fr}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Durée en français (ex: 1 heure)"
                />
              </div>
              <div className="form-group">
                <label htmlFor="description_fr" className="form-label">
                  Description (French)
                </label>
                <textarea
                  id="description_fr"
                  name="description_fr"
                  value={formData.description_fr}
                  onChange={handleChange}
                  className="form-input form-textarea"
                  placeholder="Description du service en français"
                  rows="4"
                />
              </div>
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hasAudio"
                checked={formData.hasAudio}
                onChange={handleChange}
                className="checkbox-input"
              />
              <span className="checkbox-text">Has Audio Component</span>
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  {isEditMode ? 'Updating Service...' : 'Adding Service...'}
                </>
              ) : (
                isEditMode ? 'Update Service' : 'Add Service'
              )}
            </button>
            <button
              type="button"
              className="reset-btn"
              onClick={() => {
                setFormData({
                  name: '',
                  duration: '',
                  price: '',
                  description: '',
                  type: 'Remote',
                  category: '',
                  image: '',
                  hasAudio: false,
                  pdfUrl: '',
                  audioUrl: '',
                  name_ar: '',
                  description_ar: '',
                  duration_ar: '',
                  name_ru: '',
                  description_ru: '',
                  duration_ru: '',
                  name_de: '',
                  description_de: '',
                  duration_de: '',
                  name_fr: '',
                  description_fr: '',
                  duration_fr: ''
                });
                setImageFile(null);
                setImagePreview(null);
                setPdfFile(null);
                setAudioFile(null);
                setUploadProgress(0);
                setErrors({});
                setSuccessMessage('');
                setErrorMessage('');
                // Reset file inputs
                const imageInput = document.getElementById('image');
                if (imageInput) imageInput.value = '';
                const pdfInput = document.getElementById('pdf');
                if (pdfInput) pdfInput.value = '';
                const audioInput = document.getElementById('audio');
                if (audioInput) audioInput.value = '';
              }}
            >
              Reset Form
            </button>
          </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminAddService;

